import styles from "../css/ecoControlSensors.module.css";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import { Thermometer, CheckCircle2, AlertTriangle } from 'lucide-react';
import Button from "../../../../components/Button";
import Table from "../../../../components/Table";
import FilterButtons from "../../../../components/universal-filter-tabs";
import { useEffect, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import modalStyles from "../../../../css/modal.module.css";
import detailStyles from "../../../Inventory Managment/css/inventoryItemDetail.module.css";
import { sensorSections, medicationTableData, medicationTableColumns, sensorPerformanceData, chartData } from "../data/sensorData";

export default function EcoControlSensors() {
  const { user } = useAuth();
  const roleId = Number(user?.roleid);

  const navigate = useNavigate();
  const { sectionSlug, metricSlug } = useParams();

  const [detail, setDetail] = useState(null); // { sectionTitle, metric }
  const [showThresholds, setShowThresholds] = useState(false);
  const [minThreshold, setMinThreshold] = useState("20");
  const [maxThreshold, setMaxThreshold] = useState("25");
  const [activeTab, setActiveTab] = useState("Sensors");

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    
    // Navigate to the appropriate route
    if (tab === "Fridges") {
      navigate("/inventory/eco-control-fridges");
    } else {
      navigate("/inventory/eco-control-sensors");
    }
  };

  const tabs = ["Sensors", "Fridges"];

  const getState = (metric) => {
    if (metric.type === "Temperature") {
      // Example threshold: <= 8 normal (cold storage), > 18 critical
      if (metric.value > 18) return { label: "Critical", className: styles.critical, dot: "warn" };
      return { label: "Normal", className: styles.normal, dot: "ok" };
    }
    if (metric.type === "Humidity") {
      // 40-60 considered normal
      const normal = metric.value >= 40 && metric.value <= 60;
      return { label: normal ? "Normal" : "Critical", className: normal ? styles.normal : styles.critical, dot: normal ? "ok" : "warn" };
    }
    return { label: "Normal", className: styles.normal, dot: "ok" };
  };

  const slugify = (value) =>
    value
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");

  useEffect(() => {
    if (!sectionSlug || !metricSlug) {
      setDetail(null);
      return;
    }

    const section = sensorSections.find(
      (s) => slugify(s.title) === sectionSlug
    );
    if (!section) {
      setDetail(null);
      return;
    }

    const metric = section.metrics.find(
      (m) => slugify(m.type) === metricSlug
    );

    if (metric) {
      setDetail({ sectionTitle: section.title, metric });
    } else {
      setDetail(null);
    }
  }, [sectionSlug, metricSlug]);

  const handleCardClick = (section, metric) => {
    const sectionPath = slugify(section.title);
    const metricPath = slugify(metric.type);
    navigate(`/inventory/eco-control-sensors/${sectionPath}/${metricPath}`);
  };

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.dashboardLayout}>
        <main className={styles.main}>
          <div className={styles.container}>
            {/* Filter Tabs - Only show to Director (role ID 2) */}
            {roleId === 2 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                <FilterButtons
                  buttons={tabs}
                  activeButton={activeTab}
                  onButtonClick={handleTabClick}
                />
              </div>
            )}

            {!detail && sensorSections.map((section, idx) => (
              <section key={idx} className={styles.section}>
                <h2 className={styles.sectionTitle}>{section.title}</h2>
                <div className={styles.gridWrapper}>
                  <div className={styles.grid}>
                  {section.metrics.map((m, i) => {
                    const state = getState(m);
                    return (
                      <div
                        key={i}
                        className={`${styles.card} ${styles.clickable}`}
                        onClick={() => handleCardClick(section, m)}
                      >
                        <div className={styles.cardHeader}>
                          <span className={styles.metricTitle}>
                            {m.type === "Temperature" && (
                              <Thermometer size={28} strokeWidth={3} className={styles.tempIcon} />
                            )}
                            {m.type}
                          </span>
                          <span className={state.dot === "ok" ? styles.okDot : styles.warnDot}>
                            {state.dot === "ok" ? (
                              <CheckCircle2 size={22} color="#16a34a" aria-hidden />
                            ) : (
                              <AlertTriangle size={29} color="#ef4444" aria-hidden />
                            )}
                          </span>
                        </div>
                        <div className={styles.metricRow}>
                          <div className={styles.metricValue}>{m.value}{m.unit === "%" ? "%" : ""}</div>
                          {m.unit !== "%" && <div className={styles.metricUnit}>{m.unit}</div>}
                        </div>
                        <div className={`${styles.state} ${state.className}`}>{state.label}</div>
                        <div className={styles.updated}>Last Update: {m.updated}</div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              </section>
            ))}

            {detail && (
              <section className={styles.section}>
                <div className={styles.gridWrapper}>
                  <div className={styles.detailContainer}>
                    {/* Top header status card - title on first row, details below */}
                    <div className={`${styles.card} ${styles.detailHeader}`}>
                      <div className={styles.detailHeaderTop}>
                        <h2 className={`${styles.sectionTitle} ${styles.noTopMargin}`}>{detail.sectionTitle}</h2>
                      </div>
                      <div className={styles.detailHeaderBottom}>
                        <div className={styles.rowCenterGap12}>
                          <div className={styles.rowCenterGap8}>
                            <Thermometer size={36} strokeWidth={3} className={styles.tempIcon} />
                            <span className={styles.fw800}>{detail.metric.type}</span>
                          </div>
                          <div className={styles.rowCenterGap8}>
                            <span className={styles.metricValue}>{detail.metric.value}</span>
                            <span className={styles.metricUnit}>{detail.metric.unit}</span>
                          </div>
                          <span className={styles.mutedText}>Last Update: {detail.metric.updated}</span>
                        </div>
                        <div className={`${styles.state} ${getState(detail.metric).className}`}>{getState(detail.metric).label}</div>
                      </div>
                    </div>

                    {/* Actions and Sensor Performance */}
                    <div className={styles.gridTwoCols}>
                      <div className={`${styles.card} ${roleId === 4 ? styles.compactActions : ""}`}>
                        <h3 className={styles.noTopMargin}>Actions</h3>
                        <div className={styles.gridGap8}>
                          <Button variant="primary">Refresh Data</Button>
                          <Button variant="primary" onClick={() => setShowThresholds(true)}>Adjust Thresholds</Button>
                        </div>
                      </div>
                      <div className={`${styles.card} ${roleId === 4 ? styles.sensorCompact : ""}`}>
                        <h3 className={styles.noTopMargin}>Sensor Performance</h3>
                        <div className={styles.gridThreeCols}>
                          <div className={`${styles.card} ${styles.centerCard}`}>
                            <div className={styles.rowCenterGap8}><CheckCircle2 size={22} strokeWidth={3} color="#3b82f6" /><div className={styles.muted700}>Up-Time</div></div>
                            <div className={`${styles.fs28} ${styles.fw900}`}>{sensorPerformanceData.upTime}</div>
                          </div>
                          <div className={`${styles.card} ${styles.centerCard}`}>
                            <div className={styles.rowCenterGap8}><Thermometer size={22} strokeWidth={3} color="#3b82f6" /><div className={styles.muted700}>Last Updated</div></div>
                            <div className={`${styles.fs24} ${styles.fw900}`}>{sensorPerformanceData.lastCalibrated}</div>
                          </div>
                          <div className={`${styles.card} ${styles.centerCard}`}>
                            <div className={styles.rowCenterGap8}><AlertTriangle size={22} strokeWidth={3} color="#3b82f6" /><div className={styles.muted700}>Accuracy</div></div>
                            <div className={`${styles.fs24} ${styles.fw900}`}>{sensorPerformanceData.accuracy}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Storage Medication table */}
                    <h3 className={`${styles.sectionTitle} ${styles.mt18}`}>Storage 3 Medication</h3>
                    {roleId === 4 ? (
                      // For Pharmacy Admin (role 4), render the table without the white card wrapper
                      <Table
                        columns={medicationTableColumns}
                        data={medicationTableData}
                      />
                    ) : (
                      <div className={styles.card}>
                        <Table
                          columns={medicationTableColumns}
                          data={medicationTableData}
                        />
                      </div>
                    )}

                    {/* Temperature Trend chart */}
                    <h3 className={`${styles.sectionTitle} ${styles.mt24}`}>Temperature Trend (Last 24 hours)</h3>
                    <div className={`${styles.card} ${styles.cardPad}`}>
                      <div className={styles.seriesLegendRow}>
                        <span className={styles.legendHumidity}>{chartData.humidity}</span>
                        <span className={styles.legendTemp}>{chartData.temperature}</span>
                      </div>
                      {/* Simple inline SVG line chart to avoid creating new components */}
                      <svg viewBox="0 0 600 200" className={styles.trendChart24h}>
                        <line x1="40" y1="10" x2="40" y2="180" stroke="#111" />
                        <line x1="40" y1="180" x2="580" y2="180" stroke="#111" />
                        {/* Y-axis ticks and labels (sample values) */}
                        <line x1="37" y1="180" x2="43" y2="180" stroke="#111" />
                        <text x="35" y="184" fontSize="12" textAnchor="end">0</text>
                        <line x1="37" y1="150" x2="43" y2="150" stroke="#111" />
                        <text x="35" y="154" fontSize="12" textAnchor="end">10</text>
                        <line x1="37" y1="120" x2="43" y2="120" stroke="#111" />
                        <text x="35" y="124" fontSize="12" textAnchor="end">15</text>
                        <line x1="37" y1="90" x2="43" y2="90" stroke="#111" />
                        <text x="35" y="94" fontSize="12" textAnchor="end">20</text>
                        <line x1="37" y1="60" x2="43" y2="60" stroke="#111" />
                        <text x="35" y="64" fontSize="12" textAnchor="end">25</text>
                        <line x1="37" y1="30" x2="43" y2="30" stroke="#111" />
                        <text x="35" y="34" fontSize="12" textAnchor="end">30</text>
                        {/* Humidity (green) */}
                        <polyline fill="none" stroke="#10b981" strokeWidth="3" points="40,150 200,130 360,110 520,90" />
                        {/* Temperature (purple) */}
                        <polyline fill="none" stroke="#8b5cf6" strokeWidth="3" points="40,160 200,170 360,150 520,150" />
                      </svg>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Adjust Thresholds Modal */}
            {detail && showThresholds && (
              <div className={modalStyles.modalPopup} onClick={() => setShowThresholds(false)}>
                <div className={modalStyles.modalContent} onClick={(e) => e.stopPropagation()}>
                  <button className={modalStyles.closeBtn} onClick={() => setShowThresholds(false)} aria-label="Close">×</button>
                  <div className={detailStyles.card}>
                    <h2 className={styles.noTopMargin}>Adjust Thresholds for {detail.metric.type}</h2>
                    <div className={detailStyles.row}>
                      <div className={detailStyles.field}>
                        <label>Min</label>
                        <input type="number" value={minThreshold} onChange={(e) => setMinThreshold(e.target.value)} />
                      </div>
                      <div className={detailStyles.field}>
                        <label>Max</label>
                        <input type="number" value={maxThreshold} onChange={(e) => setMaxThreshold(e.target.value)} />
                      </div>
                    </div>
                    <div className={`${styles.rowGap12} ${styles.mt12}`}>
                      <Button variant="primary" onClick={() => setShowThresholds(false)}>Save Thresholds</Button>
                      <Button variant="secondary" onClick={() => setShowThresholds(false)}>Cancel</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
